import React, { useState, useEffect } from 'react';
import _ from 'lodash';
import Field from '../../Fields/Field';
import MultiSelectDropdown from '../../Fields/MultiSelectDropdown';
import InputDateField from '../../Fields/InputDateField';
import ActionButton from '../../Common/ActionButton';
import InputTextField from '../../Fields/InputTextField';
import InputSelectField from '../../Fields/InputSelectField';
import useReport from '../../../Hooks/useReport';
import PaymentReports from '../../../Utils/paymentReports'
import './PaymentReportsForm.scss';
import featureFlags from '../../../featureFlags';
import NoticeBlock from '../../Common/NoticeBlock';
import MessageList from '../../Common/MessageList';
import { validateFilters } from '../../../Validators/PaymentReportsForm';

const rateTypesValueRenderer = (selected, options) => {
  let generateItemsSelectedText = () =>{
    const selectedWithOmittedOption = selected.filter(({label})=>{
      return label !== "Validation & Other Types";
    });
    return (selectedWithOmittedOption.length === 1) ? selectedWithOmittedOption[0].label : selectedWithOmittedOption.length + " items selected"
  }
  return selected.length === options.length ? "All selected" :
    !selected.length ? "None selected" :
      generateItemsSelectedText();
};

function PaymentReportsForm({ facilityId }) {
  const {
    filterValues,
    downloadCSVReport,
    downloadPDFReport,
    inputOnChange,
    inputOnDropDownChange,
    resetFilters,
    isDownloadingCSV,
    isDownloadingPDF,
    errors,
    setErrors
  } = useReport(PaymentReports, facilityId);

  const validateFilterValues = (reportFormat) => {
    try {
      validateFilters(filterValues);
      if (reportFormat === 'CSV') {
        downloadCSVReport(true);
      } else {
        downloadPDFReport(true);
      }
    } catch (error) {
      setErrors([error.message]);
    }
  }

  const [selectedReportType, setSelectedReportType] = useState(null);
  const [selectedRateTypes, setSelectedRateTypes] = useState([]);
  const [isValidationOtherSelected, setIsValidationOtherSelected] = useState(true);
  const [isValidationAutoSelectionInProgress, setIsValidationAutoSelectionInProgress] = useState(false);
  const [selectedDateOrder, setSelectedDateOrder] = useState(null);

  const validationOptions = [
    { label: "Prepaid Validation", value: "PREPAID_VALIDATION" },
    { label: "Bill Back Validation", value: "BILL_BACK_VALIDATION" },
    { label: "Hotel Storage", value: "HOTEL_STORAGE" },
    { label: "Office/Retail Rent", value: "OFFICE_RETAIL_RENT" },
    { label: "Miscellaneous", value: "MISCELLANEOUS" }
  ];

  useEffect(()=> {
    if (!selectedReportType &&
      (filterValues.reportType ||
        filterValues.payorId ||
        filterValues.payorBillingName ||
        filterValues.rateTypes.length !== 7 ||
        filterValues.transactionStartDate ||
        filterValues.transactionEndDate ||
        filterValues.dateOrder ||
        filterValues.applicationStartDate ||
        filterValues.applicationEndDate
      )) {
        onReset();
        return;
    }

    //update Rate Type selection
    if (selectedReportType) {
      setSelectedRateTypes([
        ...(featureFlags.PAYMENT_SUMMARY && selectedReportType === 'SUMMARY' ? [{ label: "Applied Credit", value: "APPLIED_CREDIT" }] : []),
        { label: "Monthly", value: "MONTHLY" },
        { label: "Validation & Other Types", value: "VALIDATION_OTHER" },
        ...validationOptions
      ]);
    }

    //update Sub Name and Sub ID filters
    if (selectedReportType === "RECONCILIATION") {
      filterValues.payorId = "";
      filterValues.payorBillingName = "";
    }

    //update Date Order selection
    if (selectedReportType === "RECONCILIATION") {
      setSelectedDateOrder("TRANSACTION_DATE");
    }
    else if (selectedReportType === "DETAIL" || selectedReportType === "SUMMARY") {
      setSelectedDateOrder("APPLICATION_DATE");
    }
    else if (!selectedReportType){
      setSelectedDateOrder("");
    }

    //update Application Date fields
    if (selectedReportType === "RECONCILIATION") {
      filterValues.applicationStartDate = null;
      filterValues.applicationEndDate = null;
    }

    inputOnDropDownChange('reportType', selectedReportType);
  }, [selectedReportType])

  useEffect(()=> {
    const updatedIsValidationOtherSelected = selectedRateTypes.some(type => {
      return type.value === "VALIDATION_OTHER";
    });

    //"Validation & Other Types" toggled
    if (updatedIsValidationOtherSelected !== isValidationOtherSelected) {
      //ignore toggled logic if it was toggled automatically
      if (!isValidationAutoSelectionInProgress) {
        //rebuild selected rate types, ignoring the final five options
        let updatedSelectedRateTypes = [];
        selectedRateTypes.forEach((option) => {
          const validationOptions = [
            'PREPAID_VALIDATION',
            'BILL_BACK_VALIDATION',
            'HOTEL_STORAGE',
            'OFFICE_RETAIL_RENT',
            'MISCELLANEOUS',
          ];

          if (!validationOptions.includes(option.value)) {
            updatedSelectedRateTypes.push(option);
          }
        });

        //select all or none of the final five options
        if (updatedIsValidationOtherSelected) {
          validationOptions.forEach((option)=>{
            updatedSelectedRateTypes.push(option);
          });
        }

        if (updatedSelectedRateTypes.length !== selectedRateTypes.length) {
          setSelectedRateTypes(updatedSelectedRateTypes);
        } else {
          setSelectedRateTypes(updatedSelectedRateTypes)
        }
      }
      else{
        setIsValidationAutoSelectionInProgress(false);
      }
      setIsValidationOtherSelected(updatedIsValidationOtherSelected);
      return;
    }

    const validationOptionValues = validationOptions.map(({ value }) => value);
    const selectedValues = selectedRateTypes.map(({ value }) => value);
    const isAllValidationOptionsSelected = validationOptionValues.every(value => selectedValues.includes(value));

    //"Validation & Other Types" status triggered by other toggled option
    if (!isAllValidationOptionsSelected && updatedIsValidationOtherSelected) {
      const updatedSelectedRateTypes = _.clone(selectedRateTypes).filter((rateType) => {
        return rateType.value !== "VALIDATION_OTHER";
      });
      setIsValidationAutoSelectionInProgress(true);
      setSelectedRateTypes(updatedSelectedRateTypes);
    }
    else if (isAllValidationOptionsSelected && !updatedIsValidationOtherSelected) {
      const updatedSelectedRateTypes = _.clone(selectedRateTypes);
      updatedSelectedRateTypes.push({ label: "Validation & Other Types", value: "VALIDATION_OTHER" });
      setSelectedRateTypes(updatedSelectedRateTypes);
    }

    inputOnDropDownChange("rateTypes", selectedRateTypes);
  }, [selectedRateTypes])

  useEffect(()=> {
    inputOnDropDownChange('dateOrder', selectedDateOrder);
  }, [selectedDateOrder])

  const reportTypeOptions = [
    { label: "Select", value: "" },
    { label: "Payment Summary Report", value: "SUMMARY" },
    { label: "Payment Detail Report", value: "DETAIL" },
    { label: "Payment Reconciliation Report", value: "RECONCILIATION" }
  ];

  const getRateOptions = () => {
    return [
      ...(featureFlags.PAYMENT_SUMMARY && selectedReportType === 'SUMMARY' ? [{ label: "Applied Credit", value: "APPLIED_CREDIT" }] : []),
      { label: "Monthly", value: "MONTHLY" },
      { label: "Validation & Other Types", value: "VALIDATION_OTHER" },
      ...validationOptions
    ];
  }

  const dateOrderOptions = [
    { label: "Select", value: "" },
    { label: "Transaction Date", value: "TRANSACTION_DATE" },
    { label: "Application Date", value: "APPLICATION_DATE" },
  ];

  const onReset = ()=> {
    setSelectedReportType("");
    setSelectedRateTypes([
      ...(featureFlags.PAYMENT_SUMMARY && selectedReportType === 'SUMMARY' ? [{ label: "Applied Credit", value: "APPLIED_CREDIT" }] : []),
      { label: "Monthly", value: "MONTHLY" },
      { label: "Validation & Other Types", value: "VALIDATION_OTHER" },
      ...validationOptions
    ]);
    setSelectedDateOrder("");
    resetFilters();
  }

  const isReportFilterDisabled = selectedReportType !== "SUMMARY";
  const isApplicationDateFieldsDisabled = selectedReportType === "RECONCILIATION";

  return (
    <div className="parkchirp--payments-report--filter-form-container">
      <div className="parkchirp--payments-report--filter-form">
        <h2>Payment Reports</h2>

        <div className="parkchirp--payments-report--filter-inputs">

          <div className="parkchirp--payments-report--filter-input-block">
            <Field label="Report Type"
              id="parkchirp--payments-report-filter--report-type"
              layout="columns">
              <InputSelectField
                inputId="parkchirp--payments-report-filter--report-type--select-field"
                options={reportTypeOptions}
                value={selectedReportType}
                onChange={setSelectedReportType}
                editMode={true}
              />
            </Field>

            <Field label="Sub. ID"
              id="parkchirp--payments-report-filter--payor-id"
              layout="columns">
              <InputTextField
                inputId="parkchirp--payments-report-filter--payor-id"
                value={filterValues.payorId}
                onChange={inputOnChange('payorId')}
                editMode={true}
                disabledMode={selectedReportType === "RECONCILIATION"}
              />
            </Field>

            <Field label="Sub. Name"
              id="parkchirp--payments-report-filter--billing-name"
              layout="columns">
              <InputTextField
                inputId="parkchirp--payments-report-filter--billing-name"
                value={filterValues.payorBillingName}
                onChange={inputOnChange('payorBillingName')}
                editMode={true}
                disabledMode={selectedReportType === "RECONCILIATION"}
              />
            </Field>

          </div>
          <div className="parkchirp--payments-report--filter-input-block">

          <Field label="Rate Type"
              id="parkchirp--payments-report-filter--rate-type"
              layout="columns">
              <MultiSelectDropdown
                id={"parkchirp--payments-report-filter--rate-type--multi-select-dropdown"}
                label="Rate Type"
                labelWidth={"sm"}
                options={getRateOptions()}
                selected={selectedRateTypes}
                onChange={setSelectedRateTypes}
                disabledMode={isReportFilterDisabled}
                hasSelectAll={true}
                valueRenderer = {rateTypesValueRenderer}
              />
            </Field>

            <Field label="Transaction Start Date"
              id="parkchirp--payments-report-filter--transaction-start-date"
              layout="columns">
              <InputDateField inputId="parkchirp--payments-report-filter--transaction-start-date"
                value={filterValues.transactionStartDate}
                onChange={inputOnChange('transactionStartDate')}
                editMode={true}
              />
            </Field>

            <Field label="Transaction End Date"
              id="parkchirp--payments-report-filter--transaction-end-date"
              layout="columns">
              <InputDateField inputId="parkchirp--payments-report-filter--transaction-end-date"
                value={filterValues.transactionEndDate}
                onChange={inputOnChange('transactionEndDate')}
                editMode={true}
              />
            </Field>

          </div>

          <div className="parkchirp--payments-report--filter-input-block">
            <Field label="Date Order"
              id="parkchirp--payments-report-filter--date-order"
              layout="columns">
              <InputSelectField
                inputId="parkchirp--payments-report-filter--date-order--select-field"
                options={dateOrderOptions}
                value={selectedDateOrder}
                onChange={setSelectedDateOrder}
                editMode={true}
                disabledMode={selectedReportType === "RECONCILIATION"}
              />
            </Field>

            <Field label="Application Start Date"
              id="parkchirp--payments-report-filter--application-start-date"
              layout="columns">
              <InputDateField inputId="parkchirp--payments-report-filter--application-start-date"
                value={filterValues.applicationStartDate}
                onChange={inputOnChange('applicationStartDate')}
                editMode={true}
                disabledMode={isApplicationDateFieldsDisabled}
              />
            </Field>

            <Field label="Application End Date"
              id="parkchirp--payments-report-filter--application-end-date"
              layout="columns">
              <InputDateField inputId="parkchirp--payments-report-filter--application-end-date"
                value={filterValues.applicationEndDate}
                onChange={inputOnChange('applicationEndDate')}
                editMode={true}
                disabledMode={isApplicationDateFieldsDisabled}
              />
            </Field>
          </div>
        </div>

        <div className="parkchirp--payments-report--filter-actions">
          <ActionButton label="Download CSV"
            classNames={["button", "button-primary parkchirp--payments-report--filter-action"]}
            onClick={() => { validateFilterValues('CSV')} }
            inProgressState={isDownloadingCSV}
            disabled={!selectedReportType}
          />

          {featureFlags.PAYMENT_PDF &&
          <ActionButton label="Download PDF"
            classNames={["button", "button-primary parkchirp--payments-report--filter-action"]}
              onClick={() => { validateFilterValues('PDF')} }
            inProgressState={isDownloadingPDF}
            disabled={!selectedReportType}
          />
          }

          <button type="button"
            className="button parkchirp--payments-report--filter-action"
            onClick={onReset}
          >
            Reset
          </button>
        </div>
        {!!errors.length &&
          <div className="parkchirp--payments-report--filter-errors">
            <NoticeBlock type="error" containerClasses={['parkchirp--sidebar-block']}>
              <MessageList
                heading={`An error occurred while attempting to generate the report.`}
                messages={errors}
              />
            </NoticeBlock>
          </div>
        }
      </div>
    </div>
  )
}

export default React.memo(PaymentReportsForm);

//for testing
export { rateTypesValueRenderer };